import React, { useState, useRef } from 'react';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { adminFetch } from '@/utils/adminAuth';
import { Image as ImageIcon, Upload, Link as LinkIcon, X, Loader2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Page-scoped editable image. Uses PageEditModeContext (per-slug drafts).
 * Renders a plain <img loading="lazy" decoding="async"> when not editing. In edit mode, hovering the image
 * reveals a "Change image" overlay that opens a modal offering upload or
 * paste-URL. The resulting URL is written to `path` in the page config.
 *
 * Upload goes through the existing admin hero-slides endpoint (auto WebP
 * compression) — same pipeline the homepage editor uses.
 */
export const PageEditableImage = ({
  path,
  fallback = '',
  alt = '',
  className = '',
  ...rest
}) => {
  const { enabled, getValue, update } = usePageEditMode();
  const [showModal, setShowModal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const current = getValue(path);
  const src = current || fallback;

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminFetch('/admin/hero-slides/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (!data.url) throw new Error('No URL returned');
      update(path, data.url);
      toast.success('Image uploaded');
      setShowModal(false);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSave = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    update(path, trimmed);
    toast.success('Image URL set');
    setShowModal(false);
    setUrlInput('');
  };

  const handleLibrarySelect = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;
    const item = selectedItems[0];
    const rawUrl = item.url || '';
    const fullUrl = rawUrl.startsWith('http') ? rawUrl : `${BACKEND_URL}${rawUrl}`;
    update(path, fullUrl);
    toast.success('Image selected from library');
    setShowLibrary(false);
    setShowModal(false);
  };

  if (!enabled) {
    return <img loading="lazy" decoding="async" src={src} alt={alt} className={className} {...rest} />;
  }

  return (
    <>
      <div className="relative group w-full h-full" data-edit-safe>
        <img loading="lazy" decoding="async" src={src} alt={alt} className={className} {...rest} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setUrlInput(src || ''); setShowModal(true); }}
          className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/0 hover:bg-black/60 transition-colors text-white opacity-0 group-hover:opacity-100"
          data-edit-safe
        >
          <div className="flex flex-col items-center gap-2">
            <ImageIcon className="w-8 h-8" />
            <span className="text-sm font-semibold">Change image</span>
          </div>
        </button>
        <div className="absolute top-2 right-2 pointer-events-none">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-yellow-400 text-black">
            Click to replace
          </span>
        </div>
      </div>

      {showModal && (
        <div
          data-edit-safe
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            data-edit-safe
            className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Change image</h3>
              <button onClick={() => setShowModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="border border-dashed border-amber-600/50 rounded-xl p-6 text-center bg-amber-500/5">
                <FolderOpen className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-300 mb-3">Pick from your existing media library</p>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setShowLibrary(true); }}
                  data-testid="page-editable-image-open-library-btn"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg inline-flex items-center gap-2"
                >
                  <FolderOpen className="w-4 h-4" /> Browse Media Library
                </button>
              </div>

              <div className="text-center text-zinc-500 text-xs">or</div>

              <div className="border border-dashed border-zinc-600 rounded-xl p-6 text-center">
                <Upload className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-300 mb-3">Upload a new image from your device</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="page-editable-image-upload-btn"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>) : 'Choose file'}
                </button>
              </div>

              <div className="text-center text-zinc-500 text-xs">or</div>

              <div>
                <label className="text-sm text-zinc-300 mb-1 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" /> Paste image URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://…"
                    data-testid="page-editable-image-url-input"
                    className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  />
                  <button
                    onClick={handleUrlSave}
                    disabled={!urlInput.trim()}
                    data-testid="page-editable-image-url-save-btn"
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg disabled:opacity-50"
                  >
                    Set
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <MediaLibraryPicker
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onSelect={handleLibrarySelect}
        allowedTypes={['image']}
        multiple={false}
      />
    </>
  );
};

export default PageEditableImage;
